import { useState, useMemo } from "react";
import { useData } from '@happyspace/sdk/data';
import { Eye, EyeOff, Pencil, Trash2, RotateCcw } from "lucide-react";
import { DEFAULT_CATEGORIES, type CategoryDef } from "../data/categories";

export default function CategoriesTab() {
  const { rows, insert, update, refetch, loading } = useData("category_icons", { autoFetch: true, limit: 500 });
  const [editingCat, setEditingCat] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editHref, setEditHref] = useState("");
  const [editIconUrl, setEditIconUrl] = useState("");

  const overrideMap = useMemo(() => {
    const map = new Map<string, any>();
    if (rows) {
      for (const row of rows as any[]) {
        const name = row.name || row.categoryName || "";
        if (name) map.set(name, row);
      }
    }
    return map;
  }, [rows]);

  const getOverride = (catName: string) => overrideMap.get(catName);

  const handleToggleHidden = async (catName: string) => {
    const existing = getOverride(catName);
    if (existing) {
      await update(existing._id || existing.id, { hidden: !existing.hidden });
    } else {
      await insert({ name: catName, hidden: true });
    }
    refetch();
  };

  const handleToggleDeleted = async (catName: string) => {
    const existing = getOverride(catName);
    if (existing) {
      if (existing.deleted) {
        await update(existing._id || existing.id, { deleted: false, hidden: false });
      } else {
        await update(existing._id || existing.id, { deleted: true });
      }
    } else {
      await insert({ name: catName, deleted: true });
    }
    refetch();
  };

  const handleRestore = async (catName: string) => {
    const existing = getOverride(catName);
    if (existing) {
      await update(existing._id || existing.id, { deleted: false, hidden: false });
      refetch();
    }
  };

  const startEdit = (cat: CategoryDef) => {
    const ov = getOverride(cat.name);
    setEditingCat(cat.name);
    setEditName(ov?.name || cat.name);
    setEditHref(ov?.href || cat.href);
    setEditIconUrl(ov?.iconUrl || cat.iconUrl || "");
  };

  const saveEdit = async () => {
    if (!editingCat) return;
    const existing = getOverride(editingCat);
    const patch: Record<string, any> = { name: editName, href: editHref };
    if (editIconUrl) patch.iconUrl = editIconUrl;
    if (existing) {
      await update(existing._id || existing.id, patch);
    } else {
      await insert({ name: editingCat, ...patch });
    }
    setEditingCat(null);
    refetch();
  };

  const categories = DEFAULT_CATEGORIES;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-xl font-extrabold text-gray-900">Category Buttons</h2>
        <p className="text-xs text-gray-500">Hide, unhide, delete, or edit category buttons shown on the homepage and View All Categories page.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold uppercase tracking-wider text-gray-500">
                <th className="px-6 py-4">Category Name</th>
                <th className="px-6 py-4">Link (href)</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm">
              {categories.map((cat) => {
                const ov = getOverride(cat.name);
                const isHidden = ov?.hidden || false;
                const isDeleted = ov?.deleted || false;
                const displayName = ov?.name || cat.name;
                const displayHref = ov?.href || cat.href;

                return (
                  <tr key={cat.name} className={`hover:bg-gray-50/50 transition-colors ${isDeleted ? "opacity-40 bg-red-50/30" : isHidden ? "opacity-60 bg-gray-50/50" : ""}`}>
                    <td className="px-6 py-4">
                      {editingCat === cat.name ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#082f6d] focus:outline-none"
                        />
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">{displayName}</span>
                          {isDeleted && <span className="text-[10px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">DELETED</span>}
                          {isHidden && !isDeleted && <span className="text-[10px] font-bold bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">HIDDEN</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {editingCat === cat.name ? (
                        <input
                          type="text"
                          value={editHref}
                          onChange={(e) => setEditHref(e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#082f6d] focus:outline-none"
                        />
                      ) : (
                        <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{displayHref}</code>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {editingCat === cat.name ? (
                        <input
                          type="text"
                          value={editIconUrl}
                          onChange={(e) => setEditIconUrl(e.target.value)}
                          placeholder="Icon URL (optional)"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-[#082f6d] focus:outline-none"
                        />
                      ) : (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${isDeleted ? "bg-red-50 text-red-600 border-red-200" : isHidden ? "bg-gray-100 text-gray-500 border-gray-200" : "bg-green-50 text-green-700 border-green-200"}`}>
                          {isDeleted ? "Deleted" : isHidden ? "Hidden" : "Visible"}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {editingCat === cat.name ? (
                          <>
                            <button onClick={saveEdit} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#51ba4f] text-white hover:bg-green-600 transition-colors">Save</button>
                            <button onClick={() => setEditingCat(null)} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors">Cancel</button>
                          </>
                        ) : isDeleted ? (
                          <button onClick={() => handleRestore(cat.name)} className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 transition-colors" title="Restore category">
                            <RotateCcw className="w-3 h-3" /> Restore
                          </button>
                        ) : (
                          <>
                            <button onClick={() => handleToggleHidden(cat.name)} className={`p-1.5 rounded-lg transition-colors ${isHidden ? "bg-gray-100 text-gray-400 hover:bg-gray-200" : "hover:bg-gray-100 text-gray-400"}`} title={isHidden ? "Unhide" : "Hide"}>
                              {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button onClick={() => startEdit(cat)} className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-600 transition-colors" title="Edit category">
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleToggleDeleted(cat.name)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-colors" title="Delete category">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {loading && (
        <div className="text-center text-sm text-gray-400 py-4">Loading overrides...</div>
      )}
    </div>
  );
}
